// src/pages/QuizPage.jsx
// Nutrition quiz styled like the legacy Typeform (LwfUuzbI): intro screen with
// the yellow starburst, one question per screen with a numbered badge and big
// serif type, letter-key option cards, underline inputs, a filterable dropdown
// for long option lists, orange OK button and corner up/down chevrons.
// All answer/branching/submit logic is unchanged.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFlow } from 'context/FlowContext';
import { stepIsVisible } from 'constants/quizConfig';
import { fetchQuizConfig, searchNutritionists, submitQuiz } from 'services/publicFlowService';
import RestrictionSearch from 'components/storefront/RestrictionSearch';
import { COLORS, FONTS } from 'styles/theme';
// RestrictionSearch styling (quiz no longer lives inside FlowLayout)
import 'styles/storefront-tokens.css';
import 'components/storefront/flow.css';

const { dark, cream, orange } = COLORS;

const SERIF = FONTS.serif;
const OPTION_BG = '#f5f2ec';
const OPTION_BORDER = '#dfd9cd';

const OK_BTN = {
  background: orange, color: '#fff', border: 'none', borderRadius: '8px',
  padding: '12px 30px', fontFamily: SERIF, fontSize: '22px', fontWeight: 700,
  cursor: 'pointer', letterSpacing: '0.5px',
};

/* ── input helpers ────────────────────────────────────────────────────────── */

// Progressive US phone formatting: (555) 123-4567
function formatPhone(v) {
  const d = String(v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// Blocks obviously fake/disposable emails, not just malformed ones.
const DISPOSABLE_DOMAINS = [
  'mailinator.com', 'tempmail.com', 'temp-mail.org', '10minutemail.com',
  'guerrillamail.com', 'yopmail.com', 'trashmail.com', 'fakeinbox.com',
  'sharklasers.com', 'getnada.com', 'dispostable.com', 'example.com', 'test.com',
];
// Same wording as the add-member modal.

export function isRealisticEmail(v) {
  const m = /^[^\s@]+@([^\s@]+\.[a-z]{2,})$/i.exec(String(v || '').trim());
  if (!m) return false;
  const domain = m[1].toLowerCase();
  if (DISPOSABLE_DOMAINS.includes(domain)) return false;
  if (/^(.)\1+@/.test(String(v).trim())) return false;
  return true;
}

// formatPhone() caps at 10 digits as the user types — a "complete" number
// means exactly 10 digits were actually entered, not just that the field is
// non-empty (e.g. "555" alone shouldn't pass as a phone number).
export function isRealisticPhone(v) {
  const digits = String(v || '').replace(/\D/g, '');
  return digits.length === 10;
}

/* ── shared logic ─────────────────────────────────────────────────────────── */

// Local (browser) calendar date as YYYY-MM-DD — matches <input type="date">'s
// value format, so it's directly comparable to it as a string.
function todayISODate() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// pregnancyTrimester ("When is your Due Date?") must be today or later;
// lactationStage ("When did you give birth?") must be today or earlier.
// Both are hardcoded by id rather than a generic config field since they're
// the only two 'date' questions and the constraint is inherent to what each
// one asks, not something a quiz config would vary per-deployment.
function dateBoundsFor(stepId) {
  const today = todayISODate();
  if (stepId === 'pregnancyTrimester') return { min: today };
  if (stepId === 'lactationStage') return { max: today };
  return {};
}

function canContinueStep(step, answers) {
  if (!step) return false;
  if (step.optional) return true;

  const val = answers[step.id];

  switch (step.type) {
    case 'statement':
      return true;
    case 'text':
      return String(val || '').trim().length >= (step.minLength ?? 1);
    case 'date': {
      if (!val) return false;
      const { min, max } = dateBoundsFor(step.id);
      if (min && val < min) return false;
      if (max && val > max) return false;
      return true;
    }
    case 'number': {
      const n = Number(val);
      if (!Number.isFinite(n)) return false;
      if (step.min != null && n < step.min) return false;
      if (step.max != null && n > step.max) return false;
      if (step.integer && !Number.isInteger(n)) return false;
      return true;
    }
    case 'single':
      return Boolean(val);
    case 'multi':
      return Array.isArray(val) && val.length > 0;
    case 'allergy_search':
      return true;
    case 'yes_no':
      return val === true || val === false;
    case 'long_text':
      return step.optional || String(val || '').trim().length > 0;
    case 'matrix':
      return (step.rows || []).every((row) => answers[step.id]?.[row.id]);
    case 'contact': {
      const fields = step.fields || [];
      const valueOf = (f) => String(answers[step.id]?.[f.id] || '').trim();
      // Leaving the whole nutritionist screen blank is itself the answer.
      if (step.optionalWhenBlank && fields.every((f) => !valueOf(f))) return true;
      return fields.every((f) => {
        const v = valueOf(f);
        if (f.required && !v) return false;
        // Filled in from our own records — not the user's to fix, and stored
        // phone numbers don't all match the 10-digit US format.
        if (f.autofill && answers.selectedNutritionist) return true;
        if (f.type === 'email' && (f.required || v)) return isRealisticEmail(v);
        if (f.type === 'tel' && (f.required || v)) return isRealisticPhone(v);
        return true;
      });
    }
    default:
      return Boolean(val);
  }
}

/* ── legacy-style label for the height dropdown ───────────────────────────── */

function displayOptionLabel(step, label) {
  if (step.id === 'height') {
    const m = /^(\d+) ft (\d+) in$/.exec(label);
    if (m) return `Your height: you are ${m[1]} feet, ${m[2]} inches tall.`;
  }
  return label;
}

/* ── building blocks ──────────────────────────────────────────────────────── */

// A…Z, then AA, AB, AC… — matches Typeform's key sequence for long option
// lists (the health-goals question has 28 options).
function optionLetter(i) {
  const A = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (i < 26) return A[i];
  return A[Math.floor(i / 26) - 1] + A[i % 26];
}

/** Substitute Typeform-style piping like {{firstName}} with actual answers. */
function pipeText(text, answers) {
  return String(text || '').replace(/\{\{\s*firstName\s*\}\}/g, (answers.name || '').trim() || 'there');
}

function LetterOption({ letter, label, selected, onClick }) {
  return (
    <button
      type="button"
      className="sfq-option"
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '14px', width: '100%',
        textAlign: 'left', background: selected ? '#fdf3e0' : OPTION_BG,
        border: `1.5px solid ${selected ? orange : OPTION_BORDER}`, borderRadius: '6px',
        padding: '13px 16px', cursor: 'pointer', marginBottom: '10px',
      }}
    >
      <span className="sfq-option__letter" style={{
        width: '26px', height: '26px', flexShrink: 0, display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center', borderRadius: '4px',
        border: `1.5px solid ${selected ? orange : '#9a958a'}`,
        background: selected ? orange : '#fff', color: selected ? '#fff' : dark,
        fontFamily: FONTS.sans, fontSize: '13px', fontWeight: 700,
      }}>{letter}</span>
      <span className="sfq-option__label" style={{ fontFamily: SERIF, fontSize: '23px', color: dark, lineHeight: 1.3 }}>{label}</span>
    </button>
  );
}

function UnderlineInput({ inputRef, ...props }) {
  return (
    <input
      ref={inputRef}
      {...props}
      className="sfq-underline"
      style={{
        width: '100%', maxWidth: '640px', background: 'transparent', border: 'none',
        borderBottom: `2px solid ${dark}`, outline: 'none', padding: '8px 2px 10px',
        fontFamily: SERIF, fontSize: '30px', color: dark, ...props.style,
      }}
    />
  );
}

const NO_AUTOFILL = {
  autoComplete: 'new-password',
  'data-lpignore': 'true',
  'data-1p-ignore': '',
  'data-bwignore': 'true',
  'data-form-type': 'other',
};

function SearchDropdown({ step, value, onSelect }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const selectedLabel = useMemo(() => {
    const opt = (step.options || []).find((o) => o.id === value);
    return opt ? displayOptionLabel(step, opt.label) : '';
  }, [step, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const opts = (step.options || []).map((o) => ({ ...o, display: displayOptionLabel(step, o.label) }));
    if (!q) return opts;
    return opts.filter((o) => o.display.toLowerCase().includes(q));
  }, [step, query]);

  return (
    <div style={{ maxWidth: '640px', position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <input
          className="sfq-underline"
          placeholder="Type or select an option"
          value={open ? query : selectedLabel}
          onFocus={() => { setOpen(true); setQuery(''); }}
          onChange={(e) => setQuery(e.target.value)}
          style={{
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: `1.5px solid ${dark}`, outline: 'none', padding: '8px 36px 10px 2px',
            fontFamily: SERIF, fontSize: '28px', color: dark,
          }}
        />
        <button
          type="button"
          aria-label={open ? 'Close options' : 'Show options'}
          onClick={() => { setOpen(!open); setQuery(''); }}
          style={{
            position: 'absolute', right: 0, bottom: '12px', background: 'none', border: 'none',
            cursor: 'pointer', fontSize: '20px', color: dark,
          }}
        >{open ? '▴' : '▾'}</button>
      </div>
      {open && (
        <div style={{
          maxHeight: '300px', overflowY: 'auto', marginTop: '10px',
          background: cream, borderRadius: '8px', boxShadow: '0 10px 30px rgba(0,0,0,0.10)',
          padding: '6px',
        }}>
          {filtered.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => { onSelect(opt.id); setOpen(false); setQuery(''); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: value === opt.id ? '#fdf3e0' : OPTION_BG,
                border: `1px solid ${value === opt.id ? orange : OPTION_BORDER}`,
                borderRadius: '6px', padding: '13px 16px', marginBottom: '6px', cursor: 'pointer',
                fontFamily: FONTS.sans, fontSize: '17px', color: dark,
              }}
            >
              {opt.display}
            </button>
          ))}
          {filtered.length === 0 && (
            <p style={{ fontFamily: FONTS.sans, color: COLORS.muted, padding: '10px 14px' }}>No matches</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ── nutritionist lookup ──────────────────────────────────────────────────── */

// The name box's text as typed; frontend-only, never submitted.
const NAME_KEY = '__name';

// Types straight into the field (so an RD we don't have on file is still
// submitted as free text) while offering matches from our own list; picking one
// fills and locks the rest of the group.
function NutritionistSearchInput({ value, onChange, onSelect, inputRef }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    const q = String(value || '').trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(async () => {
      const seq = ++seqRef.current;
      setLoading(true);
      const list = await searchNutritionists(q);
      if (seq !== seqRef.current) return; // a later keystroke already won
      setResults(list);
      setLoading(false);
    }, 250);
    return () => clearTimeout(timer);
  }, [value, open]);

  return (
    <div style={{ position: 'relative' }}>
      <UnderlineInput
        inputRef={inputRef}
        type="text"
        {...NO_AUTOFILL}
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-autocomplete="list"
        placeholder="Start typing to search our dietitians"
        value={value}
        onChange={(e) => { setOpen(true); onChange(e.target.value); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        style={{ fontSize: '24px' }}
      />
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', zIndex: 20, left: 0, right: 0, marginTop: '6px',
          maxHeight: '260px', overflowY: 'auto', background: cream, borderRadius: '8px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)', padding: '6px',
        }}>
          {results.map((n) => (
            <button
              key={n.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onSelect(n); }}
              style={{
                display: 'flex', justifyContent: 'space-between', gap: '12px', width: '100%',
                textAlign: 'left', background: OPTION_BG, border: `1px solid ${OPTION_BORDER}`,
                borderRadius: '6px', padding: '10px 14px', marginBottom: '6px', cursor: 'pointer',
                fontFamily: FONTS.sans, fontSize: '16px', color: dark,
              }}
            >
              <span>{n.name}</span>
              {n.company && <span style={{ color: COLORS.muted, fontSize: '14px' }}>{n.company}</span>}
            </button>
          ))}
        </div>
      )}
      {open && loading && String(value || '').trim().length >= 2 && results.length === 0 && (
        <p style={{ fontFamily: FONTS.sans, fontSize: '13px', color: COLORS.muted, margin: '8px 0 0' }}>Searching…</p>
      )}
    </div>
  );
}

/* ── allergies question ───────────────────────────────────────────────────── */

// `isOther` comes from the backend adapter; the label checks cover a config
// that never went through it (the offline fallback).
function isOtherAllergyOption(opt) {
  const label = String(opt.label || '').trim().toLowerCase();
  return Boolean(opt.isOther) || Boolean(opt.allowsInput) || opt.id === 'other' || /^other\b/.test(label);
}

// What a tapped option contributes to answers.restrictions: its resolved record
// id, so a card and a search hit for the same allergy can't double-count.
// Unresolved options reuse RestrictionSearch's `custom:` convention; 'none' is
// the existing sentinel utils/memberRestrictions.js strips from tag ids.
function allergyOptionValue(opt) {
  if (opt.restrictionId) return opt.restrictionId;
  if (opt.exclusive) return 'none';
  return `custom:${String(opt.label || '').trim().toLowerCase()}`;
}

// Multi-select over the question's fixed options, with the searchable picker
// inside the last "Other" row. answers.restrictions / restrictionLabels stay
// the only state either way, so everything downstream is unchanged.
function AllergyQuestion({ step, selected, labels, onChange }) {
  const [otherOpen, setOtherOpen] = useState(false);

  const allOptions = step.options || [];
  const otherOption = allOptions.find(isOtherAllergyOption);
  const cardOptions = allOptions.filter((o) => !isOtherAllergyOption(o));

  const cardIds = cardOptions.map(allergyOptionValue);
  const exclusiveIds = cardOptions.filter((o) => o.exclusive).map(allergyOptionValue);
  const searchedIds = selected.filter((id) => !cardIds.includes(id));
  // Already offered as their own card — don't list them in the search too.
  const cardRecordIds = cardOptions.map((o) => o.restrictionId).filter(Boolean);
  const otherSelected = otherOpen || searchedIds.length > 0;

  const toggleCard = (opt) => {
    const id = allergyOptionValue(opt);
    const nextLabels = { ...labels, [id]: opt.restrictionName || opt.label };
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id), nextLabels);
      return;
    }
    // "No allergies" is mutually exclusive with every other answer, both ways.
    if (opt.exclusive) {
      setOtherOpen(false);
      onChange([id], nextLabels);
      return;
    }
    onChange([...selected.filter((s) => !exclusiveIds.includes(s)), id], nextLabels);
  };

  const toggleOther = () => {
    if (otherSelected) {
      setOtherOpen(false);
      onChange(selected.filter((id) => cardIds.includes(id)), labels); // searched entries go with it
      return;
    }
    setOtherOpen(true);
    onChange(selected.filter((id) => !exclusiveIds.includes(id)), labels);
  };

  // No fixed options configured — nothing to put an "Other" row after.
  if (cardOptions.length === 0) {
    return <RestrictionSearch inline selected={selected} labelMap={labels} onChange={onChange} />;
  }

  const otherLetter = optionLetter(cardOptions.length);
  const otherLabel = otherOption?.label || 'Other';

  return (
    <div style={{ maxWidth: '580px' }}>
      <p style={{ fontFamily: SERIF, fontSize: '18px', color: '#6d6e75', margin: '0 0 14px' }}>
        Choose as many as you like
      </p>
      {cardOptions.map((opt, i) => (
        <LetterOption
          key={opt.id || opt.label}
          letter={optionLetter(i)}
          label={opt.label}
          selected={selected.includes(allergyOptionValue(opt))}
          onClick={() => toggleCard(opt)}
        />
      ))}
      {otherSelected ? (
        <div style={{
          background: '#f1ede3', border: `1.5px solid ${orange}`,
          borderRadius: '6px', padding: '10px 12px', marginBottom: '10px',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '12px' }}>
            <button
              type="button"
              aria-label={`Unselect ${otherLabel}`}
              title="Click to unselect"
              onClick={toggleOther}
              style={{
                width: '26px', height: '26px', flexShrink: 0, borderRadius: '4px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                border: `1.5px solid ${orange}`, background: '#fff', color: dark,
                fontFamily: FONTS.sans, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
              }}
            >{otherLetter}</button>
            <span style={{ fontFamily: SERIF, fontSize: '23px', color: dark, lineHeight: 1.3 }}>{otherLabel}</span>
          </div>
          <RestrictionSearch
            inline
            selected={searchedIds}
            labelMap={labels}
            excludeIds={cardRecordIds}
            onChange={(ids, nextLabels) => onChange([...selected.filter((id) => cardIds.includes(id)), ...ids], nextLabels)}
          />
        </div>
      ) : (
        <LetterOption letter={otherLetter} label={otherLabel} selected={false} onClick={toggleOther} />
      )}
    </div>
  );
}

/* ── intro screen ─────────────────────────────────────────────────────────── */

function Starburst() {
  const spokes = [];
  for (let i = 0; i < 12; i += 1) {
    spokes.push(<rect key={i} x="30" y="2" width="8" height="20" rx="4" fill="#e4f222"
      transform={`rotate(${i * 30} 34 34)`} />);
  }
  return <svg width="70" height="70" viewBox="0 0 68 68" aria-hidden="true">{spokes}</svg>;
}

function IntroScreen({ onStart }) {
  return (
    <div style={{ minHeight: '78vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 6%' }}>
      <div style={{ maxWidth: '760px' }}>
        <div style={{ marginBottom: '30px' }}><Starburst /></div>
        <h1 className="sfq-question" style={{ fontFamily: SERIF, fontSize: '52px', fontWeight: 600, color: dark, margin: '0 0 30px' }}>
          Get Your Nutrition Profile.
        </h1>
        <p className="sfq-lead" style={{ fontFamily: SERIF, fontSize: '25px', lineHeight: 1.55, color: COLORS.text, margin: '0 0 36px' }}>
          <strong>3 mins</strong> to get your <strong>nutrition profile</strong> and access to your{' '}
          <strong>individually customized dishes</strong>!
        </p>
        <button type="button" onClick={onStart} style={{ ...OK_BTN, fontSize: '24px', padding: '14px 36px' }}>
          Get Started
        </button>
        <p style={{ fontFamily: SERIF, fontSize: '19px', color: COLORS.text, marginTop: '16px' }}>
          <span aria-hidden="true" style={{ marginRight: '6px' }}>◷</span>Takes 3 minutes
        </p>
      </div>
    </div>
  );
}

function QuizUnavailable({ onRetry }) {
  return (
    <div style={{ minHeight: '78vh', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 6%' }}>
      <div style={{ maxWidth: '620px' }}>
        <h1 style={{ fontFamily: SERIF, fontSize: '40px', fontWeight: 600, color: dark, margin: '0 0 20px' }}>
          The quiz isn’t available right now.
        </h1>
        <p style={{ fontFamily: SERIF, fontSize: '21px', lineHeight: 1.5, color: COLORS.text, margin: '0 0 32px' }}>
          We couldn’t load the questions. Please try again in a moment — or browse
          this week’s menu and order without a nutrition profile.
        </p>
        <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" onClick={onRetry} style={{ ...OK_BTN, fontSize: '20px', padding: '12px 30px' }}>
            Try Again
          </button>
          <Link to="/this-weeks-menu" style={{
            fontFamily: SERIF, fontSize: '20px', color: dark, textDecoration: 'none',
            border: `1.5px solid ${OPTION_BORDER}`, borderRadius: '8px', padding: '12px 30px',
          }}>
            See the Menu
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────────── */

export default function QuizPage() {
  const navigate = useNavigate();
  const {
    startQuizFlow,
    completeQuizForMember,
    updateHouseholdMember,
    setCustomer,
    activeMemberId,
  } = useFlow();
  const [started, setStarted] = useState(false);
  const [steps, setSteps] = useState([]);
  const [quizSource, setQuizSource] = useState(null); // null while loading, then 'live' | 'unavailable'
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const inchesRef = useRef(null);
  const continueBtnRef = useRef(null);

  const loadQuiz = useCallback(() => {
    setQuizSource(null);
    fetchQuizConfig().then(({ steps: fetchedSteps, source }) => {
      setSteps(fetchedSteps);
      setQuizSource(source);
      if (source === 'unavailable') {
        // eslint-disable-next-line no-console
        console.warn('[quiz] /api/quiz/config unavailable or empty');
      }
    });
  }, []);

  useEffect(() => {
    document.title = 'Get Your Nutrition Profile — Square Fare';
    startQuizFlow();
    loadQuiz();
  }, [startQuizFlow, loadQuiz]);

  const visibleSteps = useMemo(
    () => steps.filter((s) => stepIsVisible(s, answers)),
    [steps, answers],
  );

  const step = visibleSteps[stepIndex];
  const progress = visibleSteps.length
    ? ((stepIndex + (started ? 1 : 0)) / visibleSteps.length) * 100
    : 0;

  useEffect(() => {
    if (stepIndex >= visibleSteps.length && visibleSteps.length > 0) {
      setStepIndex(visibleSteps.length - 1);
    }
  }, [stepIndex, visibleSteps.length]);

  // Each question's content is a different height, so without this the scroll
  // position (a fixed pixel offset) stays wherever it was on the previous,
  // taller/shorter question — sometimes landing past the new question entirely.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [stepIndex]);

  useEffect(() => {
    // preventScroll: focusing a control below the fold would drag the page down with it.
    if (step?.type === 'statement') {
      continueBtnRef.current?.focus({ preventScroll: true });
    } else {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [stepIndex, started, step]);

  // Pregnancy/lactation both imply the biological sex used in the nutrition
  // math — pre-fill 'sex' as soon as either is selected on the 'goals'
  // screen (which comes before 'sex' in both the live and fallback step
  // order) so it's already answered correctly by the time the user gets
  // there, instead of asking something the quiz already knows.
  useEffect(() => {
    const goals = answers.goals || [];
    const impliesFemale = goals.includes('i-am-pregnant') || goals.includes('i-am-lactating') || goals.includes('i-am-postpartum-/-lactating');
    if (impliesFemale && answers.sex !== 'female') {
      setAnswer('sex', 'female');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers.goals]);

  const setAnswer = (stepId, value) => {
    setAnswers((prev) => ({ ...prev, [stepId]: value }));
  };

  const toggleMulti = (stepId, optionId) => {
    setAnswers((prev) => {
      const current = new Set(prev[stepId] || []);
      if (optionId === 'no-conditions') return { ...prev, [stepId]: ['no-conditions'] };
      current.delete('no-conditions');
      if (current.has(optionId)) current.delete(optionId);
      else current.add(optionId);
      return { ...prev, [stepId]: [...current] };
    });
  };

  const setMatrixRow = (stepId, rowId, optionId) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), [rowId]: optionId },
    }));
  };

  const setContactField = (stepId, fieldId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: { ...(prev[stepId] || {}), [fieldId]: value },
    }));
  };

  // Picking a dietitian from our list fills every field the config marked with
  // an `autofill` key; clearing empties them again for free-text entry.
  const applyNutritionist = (step, nutritionist) => {
    setAnswers((prev) => {
      const group = { ...(prev[step.id] || {}) };
      delete group[NAME_KEY];
      (step.fields || []).forEach((f) => {
        if (f.autofill) group[f.id] = (nutritionist && nutritionist[f.autofill]) || '';
      });
      return { ...prev, [step.id]: group, selectedNutritionist: nutritionist || undefined };
    });
  };

  // One name box split back into the config's first/last fields (last word → last name).
  const setNutritionistName = (step, first, last, value) => {
    const words = String(value).replace(/\s+/g, ' ').trim().split(' ').filter(Boolean);
    const lastName = words.length > 1 ? words.pop() : '';
    setAnswers((prev) => ({
      ...prev,
      [step.id]: {
        ...(prev[step.id] || {}), [NAME_KEY]: value, [first.id]: words.join(' '), [last.id]: lastName,
      },
    }));
  };

  // heightInches renders as two feet/inches inputs (see below) but must still
  // submit as a single combined total-inches number — quizAPI-control parses it
  // via `answer // 12` / `answer % 12` (quiz.py), with no concept of two
  // fields. Raw per-part text lives under answers.heightParts so each input
  // can be edited independently (including transiently blank); the combined
  // answers.heightInches is only (re)written once both parts parse to a sane
  // feet (0-8) / inches (0-11) value, otherwise it's cleared so the normal
  // 'number' canContinueStep validation blocks continuing on a partial answer.
  const setHeightPart = (part, value) => {
    setAnswers((prev) => {
      const parts = { ...(prev.heightParts || {}), [part]: value };
      const feet = Number(parts.feet);
      const inches = Number(parts.inches);
      const valid = parts.feet !== '' && parts.inches !== undefined && parts.inches !== ''
        && Number.isInteger(feet) && feet >= 0 && feet <= 8
        && Number.isInteger(inches) && inches >= 0 && inches <= 11;
      const next = { ...prev, heightParts: parts };
      if (valid) next.heightInches = feet * 12 + inches;
      else delete next.heightInches;
      return next;
    });
  };

  // nutritionistForm is also type 'contact' and sorts earlier, so match on the field, not position.
  const customerContactStepId = useMemo(
    () => steps.find((st) => st.type === 'contact' && (st.fields || []).some((f) => f.id === 'email'))?.id,
    [steps],
  );

  const canContinue = canContinueStep(step, answers);

  // Intentionally recreated each render so the document-level Enter listener
  // below always closes over the latest canContinue/busy/step, not a stale
  // useCallback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onNext = async () => {
    if (!canContinue || busy) return;
    const lastStep = stepIndex >= visibleSteps.length - 1;
    if (!lastStep) {
      setStepIndex((i) => i + 1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await submitQuiz(answers);
      const targetId = activeMemberId || 'member-self';
      completeQuizForMember(targetId, { ...result, answers });
      const contactEmail = customerContactStepId
        ? answers[customerContactStepId]?.email
        : undefined;
      if (answers.name?.trim()) {
        updateHouseholdMember(targetId, {
          name: answers.name.trim(),
          // The client's own picks plus what the server stored for them (e.g. cheese and fatty
          // meats for high cholesterol), so the summary matches what the email promises.
          restrictionTags: [...new Set([...(answers.restrictions || []), ...(result?.profile?.restrictions || [])])],
          restrictionLabels: { ...(answers.restrictionLabels || {}), ...(result?.profile?.restrictionLabels || {}) },
          email: contactEmail || '',
        });
      }
      if (contactEmail) {
        setCustomer({
          name: answers.name?.trim() || '',
          email: contactEmail,
        });
      }
      // Legacy-style finale: a short animated "running your analysis" moment
      // before the results appear.
      setFinishing(true);
      setTimeout(() => {
        navigate('/quiz/results', { state: { result, memberId: targetId } });
      }, 3000);
    } catch {
      setError('Could not save quiz. Please try again.');
      setBusy(false);
    }
  };

  // Listens on `document` rather than a wrapper div's onKeyDown: a div-scoped
  // handler only fires while focus is somewhere inside it, but several step
  // types (stepper numbers, single/multi/yes_no/matrix before any option is
  // clicked, allergy_search) render with nothing auto-focused, so Enter would
  // hit document.body and never bubble down into the div at all.
  useEffect(() => {
    const handleEnter = (e) => {
      if (e.key === 'Enter' && step && step.type !== 'long_text' && step.type !== 'contact') {
        e.preventDefault();
        onNext();
      }
    };
    document.addEventListener('keydown', handleEnter);
    return () => document.removeEventListener('keydown', handleEnter);
  }, [step, onNext]);

  const isLast = stepIndex === visibleSteps.length - 1;
  const useDropdown = step && step.type === 'single' && (step.options || []).length > 8;

  return (
    <div style={{ background: cream, minHeight: '80vh' }} data-quiz-source={quizSource || undefined}>
      <style>{`
        .sfq-underline::placeholder { color: #c9c4ba; opacity: 1; }
        @keyframes sfq-hand { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes sfq-fade { from { opacity: 0; } to { opacity: 1; } }

        /* Phone layout (Claire, 10/06): one question per screen. The desktop
           sizes above are inline, so these need !important to win. Everything
           steps down one scale — 38→24 question, 23→17 options — the 38px
           left indent goes, the "press Enter" hint goes (no keyboard), and
           the statement screens stack with the photo on top at a third of
           the screen. */
        @media (max-width: 640px) {
          .sfq-wrap { padding: 20px 5% 36px !important; }
          .sfq-header { margin-bottom: 4px !important; }
          .sfq-num { margin-top: 5px !important; }
          .sfq-title, .sfq-question { font-size: 24px !important; line-height: 1.2 !important; }
          .sfq-help { font-size: 16px !important; margin: 6px 0 0 !important; }
          .sfq-lead { font-size: 18px !important; }
          .sfq-body { margin: 16px 0 0 !important; }
          .sfq-option { padding: 9px 12px !important; margin-bottom: 8px !important; gap: 10px !important; }
          .sfq-option__letter { width: 22px !important; height: 22px !important; font-size: 12px !important; }
          .sfq-option__label, .sfq-option__input { font-size: 17px !important; }
          .sfq-underline { font-size: 22px !important; padding: 4px 2px 6px !important; }
          .sfq-actions { margin-top: 18px !important; gap: 10px !important; }
          .sfq-ok { font-size: 18px !important; padding: 10px 24px !important; }
          .sfq-back { font-size: 16px !important; padding: 10px 14px !important; }
          .sfq-hint { display: none !important; }
          .sfq-statement {
            display: flex !important; flex-direction: column !important; gap: 20px !important;
            /* stretch, not the grid's center: a background-image div has no
               content, so centred it collapses to zero width and vanishes. */
            align-items: stretch !important;
            min-height: 0 !important; padding: 0 0 32px !important;
          }
          .sfq-statement__img { order: -1; width: 100%; height: 34vh !important; }
          .sfq-statement__text { padding: 0 5%; }
          .sfq-statement .sfq-question { margin-bottom: 12px !important; }
          .sfq-statement .sfq-help { margin-bottom: 18px !important; }
        }
      `}</style>

      {/* Progress track */}
      <div style={{ height: '4px', background: '#d8d4cb' }} aria-hidden="true">
        <div style={{ height: '100%', width: `${progress}%`, background: dark, transition: 'width 0.3s' }} />
      </div>

      {finishing ? (
        <div style={{
          minHeight: '70vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', textAlign: 'center', padding: '40px 6%',
          animation: 'sfq-fade 0.4s ease',
        }}>
          <svg width="86" height="86" viewBox="0 0 86 86" aria-hidden="true" style={{ marginBottom: '26px' }}>
            <circle cx="43" cy="43" r="38" fill="none" stroke={dark} strokeWidth="4" />
            <line x1="43" y1="43" x2="43" y2="16" stroke={orange} strokeWidth="4" strokeLinecap="round"
              style={{ transformOrigin: '43px 43px', animation: 'sfq-hand 1s linear infinite' }} />
            <line x1="43" y1="43" x2="60" y2="43" stroke={dark} strokeWidth="4" strokeLinecap="round"
              style={{ transformOrigin: '43px 43px', animation: 'sfq-hand 6s linear infinite' }} />
            <circle cx="43" cy="43" r="4" fill={dark} />
          </svg>
          <h1 style={{ fontFamily: SERIF, fontSize: '36px', fontWeight: 600, color: dark, margin: '0 0 12px' }}>
            Running your analysis…
          </h1>
          <p style={{ fontFamily: SERIF, fontSize: '20px', color: '#6d6e75', margin: 0 }}>
            Calculating your calories, macros and portions.
          </p>
        </div>
      ) : !started || !step ? (
        !step && steps.length === 0
          ? (quizSource === 'unavailable'
            ? <QuizUnavailable onRetry={loadQuiz} />
            : <p style={{ fontFamily: SERIF, fontSize: '22px', color: COLORS.text, textAlign: 'center', padding: '80px 20px' }}>Loading quiz…</p>)
          : <IntroScreen onStart={() => setStarted(true)} />
      ) : step.type === 'statement' ? (
        <div className="sfq-statement" style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(280px, 1fr) minmax(300px, 1.15fr)',
          alignItems: 'center', gap: '48px',
          minHeight: '76vh', padding: '30px 0 30px 6%',
        }}>
          <div className="sfq-statement__text">
            <h1 className="sfq-question" style={{ fontFamily: SERIF, fontSize: '40px', fontWeight: 500, color: dark, margin: step.helpText ? '0 0 16px' : '0 0 44px', lineHeight: 1.25 }}>
              {pipeText(step.question, answers)}
            </h1>
            {step.helpText && (
              <p className="sfq-help" style={{ fontFamily: SERIF, fontSize: '20px', color: '#6d6e75', margin: '0 0 34px', lineHeight: 1.4 }}>
                {pipeText(step.helpText, answers)}
              </p>
            )}
            <div className="sfq-actions" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
              {stepIndex > 0 && (
                <button
                  type="button"
                  className="sfq-back"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  style={{
                    background: 'none', border: `1.5px solid ${OPTION_BORDER}`, borderRadius: '8px',
                    padding: '12px 20px', fontFamily: SERIF, fontSize: '19px', color: dark, cursor: 'pointer',
                  }}
                >
                  ← Back
                </button>
              )}
              <button type="button" className="sfq-ok" ref={continueBtnRef} onClick={onNext} style={OK_BTN}>
                {step.buttonLabel || 'Continue'}
              </button>
              <span className="sfq-hint" style={{ fontFamily: FONTS.sans, fontSize: '13px', color: '#8d8e94' }}>
                press <strong>Enter ↵</strong>
              </span>
            </div>
          </div>
          {step.image && (
            <div
              className="sfq-statement__img"
              aria-hidden="true"
              style={{
                height: 'min(76vh, 660px)',
                backgroundImage: `url(${step.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            />
          )}
        </div>
      ) : (
        <div className="sfq-wrap" style={{ maxWidth: '860px', margin: '0 auto', padding: '90px 6% 140px' }}>

          {/* Question header */}
          <div className="sfq-header" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginBottom: '8px' }}>
            {step.type !== 'statement' && (
              <span className="sfq-num" style={{
                background: dark, color: '#fff', borderRadius: '5px', minWidth: '24px', height: '24px',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: FONTS.sans, fontSize: '13px', fontWeight: 700, marginTop: '14px', flexShrink: 0,
              }}>{stepIndex + 1}</span>
            )}
            <h1 className="sfq-title" style={{ fontFamily: SERIF, fontSize: '38px', fontWeight: 500, color: dark, margin: 0, lineHeight: 1.25 }}>
              {pipeText(step.question, answers)}{!step.optional && !step.optionalWhenBlank && step.type !== 'statement' && '*'}
            </h1>
          </div>
          {step.helpText && (
            <p className="sfq-help" style={{ fontFamily: SERIF, fontSize: '24px', color: '#6d6e75', margin: '6px 0 0 38px' }}>
              {step.helpText}
            </p>
          )}

          <div className="sfq-body" style={{ margin: '36px 0 0 38px' }}>

            {step.type === 'multi' && (
              <div style={{ maxWidth: '580px' }}>
                <p className="sfq-help" style={{ fontFamily: SERIF, fontSize: '18px', color: '#6d6e75', margin: '0 0 14px' }}>
                  Choose as many as you like
                </p>
                {step.options.map((opt, i) => {
                  const selected = (answers[step.id] || []).includes(opt.id);
                  const isOtherOption = opt.allowsInput || opt.id === 'other' || opt.label?.trim().toLowerCase() === 'other';
                  if (isOtherOption && selected) {
                    return (
                      <div key={opt.id} className="sfq-option" style={{
                        display: 'flex', alignItems: 'center', gap: '14px',
                        background: '#f1ede3', border: `1.5px solid ${orange}`,
                        borderRadius: '6px', padding: '10px 12px', marginBottom: '10px',
                      }}>
                        <button
                          type="button"
                          aria-label={`Unselect ${opt.label}`}
                          title="Click to unselect"
                          onClick={() => toggleMulti(step.id, opt.id)}
                          style={{
                            width: '26px', height: '26px', flexShrink: 0, borderRadius: '4px',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                            border: `1.5px solid ${orange}`, background: '#fff', color: dark,
                            fontFamily: FONTS.sans, fontSize: '13px', fontWeight: 700, cursor: 'pointer',
                          }}
                        >{optionLetter(i)}</button>
                        <input
                          autoFocus
                          placeholder="Type your answer"
                          className="sfq-underline sfq-option__input"
                          value={answers[`${step.id}OtherText`] || ''}
                          onChange={(e) => setAnswer(`${step.id}OtherText`, e.target.value)}
                          style={{
                            flex: 1, minWidth: 0, background: 'transparent', border: 'none',
                            outline: 'none', fontFamily: SERIF, fontSize: '23px', color: dark,
                          }}
                        />
                        <span
                          aria-hidden="true"
                          style={{
                            width: '36px', height: '30px', flexShrink: 0, background: orange,
                            color: '#fff', borderRadius: '6px', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '15px',
                          }}
                        >✓</span>
                      </div>
                    );
                  }
                  return (
                    <LetterOption
                      key={opt.id}
                      letter={optionLetter(i)}
                      label={opt.label}
                      selected={selected}
                      onClick={() => toggleMulti(step.id, opt.id)}
                    />
                  );
                })}
              </div>
            )}

            {step.type === 'single' && !useDropdown && (
              <div style={{ maxWidth: '580px' }}>
                {step.options.map((opt, i) => (
                  <LetterOption
                    key={opt.id}
                    letter={optionLetter(i)}
                    label={opt.label}
                    selected={answers[step.id] === opt.id}
                    onClick={() => setAnswer(step.id, opt.id)}
                  />
                ))}
              </div>
            )}

            {step.type === 'single' && useDropdown && (
              <SearchDropdown step={step} value={answers[step.id]} onSelect={(id) => setAnswer(step.id, id)} />
            )}

            {step.type === 'yes_no' && (
              <div style={{ maxWidth: '340px' }}>
                <LetterOption letter="A" label="Yes" selected={answers[step.id] === true} onClick={() => setAnswer(step.id, true)} />
                <LetterOption letter="B" label="No" selected={answers[step.id] === false} onClick={() => setAnswer(step.id, false)} />
              </div>
            )}

            {step.type === 'text' && (
              <UnderlineInput
                inputRef={inputRef}
                type="text"
                autoCapitalize={step.id === 'name' ? 'words' : 'off'}
                placeholder={step.placeholder ?? 'Type your answer here...'}
                value={answers[step.id] || ''}
                onChange={(e) => setAnswer(step.id, e.target.value)}
              />
            )}

            {step.type === 'date' && (
              <UnderlineInput
                inputRef={inputRef}
                type="date"
                min={dateBoundsFor(step.id).min}
                max={dateBoundsFor(step.id).max}
                value={answers[step.id] || ''}
                onChange={(e) => setAnswer(step.id, e.target.value)}
                style={{ maxWidth: '220px' }}
              />
            )}

            {step.type === 'number' && step.id === 'heightInches' && (
              <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                <label style={{ display: 'block' }}>
                  <span style={{ fontFamily: SERIF, fontSize: '18px', color: '#6d6e75', display: 'block', marginBottom: '6px' }}>
                    Feet
                  </span>
                  <UnderlineInput
                    inputRef={inputRef}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={8}
                    step={1}
                    placeholder={step.placeholder ?? '5'}
                    value={answers.heightParts?.feet ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      setHeightPart('feet', v);
                      // Feet is one digit; focusing inside the input event keeps the phone keyboard up.
                      if (/^[1-8]$/.test(v)) inchesRef.current?.focus({ preventScroll: true });
                    }}
                    style={{ width: '110px', fontSize: '30px' }}
                  />
                </label>
                <label style={{ display: 'block' }}>
                  <span style={{ fontFamily: SERIF, fontSize: '18px', color: '#6d6e75', display: 'block', marginBottom: '6px' }}>
                    Inches
                  </span>
                  <UnderlineInput
                    inputRef={inchesRef}
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={11}
                    step={1}
                    placeholder="8"
                    value={answers.heightParts?.inches ?? ''}
                    onChange={(e) => setHeightPart('inches', e.target.value)}
                    style={{ width: '110px', fontSize: '30px' }}
                  />
                </label>
              </div>
            )}

            {step.type === 'number' && step.id !== 'heightInches' && !step.stepper && (
              <UnderlineInput
                inputRef={inputRef}
                type="number"
                inputMode="numeric"
                min={step.min}
                max={step.max}
                step={step.integer ? 1 : undefined}
                placeholder={step.placeholder ?? 'Type your answer here...'}
                value={answers[step.id] ?? ''}
                onChange={(e) => setAnswer(step.id, e.target.value)}
              />
            )}

            {step.type === 'number' && step.stepper && (() => {
              const stepBy = step.stepper.step || 1;
              return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <input
                    ref={inputRef}
                    className="sfq-underline"
                    type="number"
                    inputMode="numeric"
                    min={step.min}
                    max={step.max}
                    step={stepBy}
                    onFocus={(e) => e.target.select()}
                    placeholder={step.placeholder}
                    value={answers[step.id] ?? ''}
                    onChange={(e) => setAnswer(step.id, e.target.value)}
                    style={{
                      fontFamily: SERIF, fontSize: '34px', color: dark, width: '130px',
                      background: 'transparent', border: 'none', outline: 'none',
                      borderBottom: `2px solid ${dark}`, padding: '4px 6px 8px',
                    }}
                  />
                </div>
              );
            })()}

            {step.type === 'long_text' && (
              <textarea
                rows={4}
                placeholder={step.placeholder ?? 'Type your answer here...'}
                value={answers[step.id] || ''}
                onChange={(e) => setAnswer(step.id, e.target.value)}
                className="sfq-underline"
                style={{
                  width: '100%', maxWidth: '640px', background: 'transparent',
                  border: 'none', borderBottom: `2px solid ${dark}`, outline: 'none',
                  padding: '8px 2px 10px', fontFamily: SERIF, fontSize: '26px', color: dark,
                  resize: 'vertical',
                }}
              />
            )}

            {step.type === 'allergy_search' && (
              <AllergyQuestion
                step={step}
                selected={answers.restrictions || []}
                labels={answers.restrictionLabels || {}}
                onChange={(ids, labels) => {
                  setAnswers((prev) => ({
                    ...prev,
                    restrictions: ids,
                    restrictionLabels: labels,
                  }));
                }}
              />
            )}

            {step.type === 'matrix' && (
              <div>
                {(step.rows || []).map((row) => (
                  <fieldset key={row.id} style={{ border: 'none', padding: 0, margin: '0 0 22px' }}>
                    <legend style={{ fontFamily: SERIF, fontSize: '23px', color: dark, marginBottom: '10px', padding: 0 }}>
                      {row.label}
                    </legend>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {row.options.map((opt) => {
                        const selected = answers[step.id]?.[row.id] === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setMatrixRow(step.id, row.id, opt.id)}
                            style={{
                              background: selected ? '#fdf3e0' : OPTION_BG,
                              border: `1.5px solid ${selected ? orange : OPTION_BORDER}`,
                              borderRadius: '6px', padding: '10px 20px', cursor: 'pointer',
                              fontFamily: SERIF, fontSize: '19px', color: dark,
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </fieldset>
                ))}
              </div>
            )}

            {step.type === 'contact' && (() => {
              const fields = step.fields || [];
              const picked = answers.selectedNutritionist;
              const firstField = fields.find((f) => f.autofill === 'firstName');
              const lastField = fields.find((f) => f.autofill === 'lastName');
              // Once a dietitian is picked, the two config fields show the record read-only.
              const mergeName = Boolean(firstField && lastField && !picked);
              const group = answers[step.id] || {};
              const nameValue = group[NAME_KEY]
                ?? [group[firstField?.id], group[lastField?.id]].filter(Boolean).join(' ');
              const nameStarted = fields.some((f) => String(group[f.id] || '').trim());
              return (
              <div style={{ maxWidth: '640px', display: 'grid', gap: '28px' }}>
                {mergeName && (
                  <label style={{ display: 'block' }}>
                    <span style={{ fontFamily: SERIF, fontSize: '21px', color: dark, display: 'block', marginBottom: '4px' }}>
                      {"Your dietitian's name"}{firstField.required && (!step.optionalWhenBlank || nameStarted) && '*'}
                    </span>
                    <NutritionistSearchInput
                      value={nameValue}
                      onChange={(v) => setNutritionistName(step, firstField, lastField, v)}
                      onSelect={(n) => applyNutritionist(step, n)}
                    />
                  </label>
                )}
                {fields.map((field) => {
                  if (mergeName && (field === firstField || field === lastField)) return null;
                  const raw = answers[step.id]?.[field.id] || '';
                  const locked = Boolean(picked) && Boolean(field.autofill);
                  const missingFromRecord = locked && !raw;
                  // On a skippable group the required markers only mean anything
                  // once the user has started filling it in.
                  const started = (step.fields || []).some((f) => String(answers[step.id]?.[f.id] || '').trim());
                  const showRequired = field.required && (!step.optionalWhenBlank || started);
                  const emailInvalid = !locked && field.type === 'email' && raw.trim() && !isRealisticEmail(raw);
                  const phoneInvalid = !locked && field.type === 'tel' && raw.trim() && !isRealisticPhone(raw);
                  return (
                    <label key={field.id} style={{ display: 'block' }}>
                      <span style={{
                        fontFamily: SERIF, fontSize: '21px', color: dark, display: 'flex',
                        alignItems: 'baseline', gap: '12px', marginBottom: '4px',
                      }}>
                        {field.label}{showRequired && '*'}
                        {locked && field.search && (
                          <button
                            type="button"
                            onClick={() => applyNutritionist(step, null)}
                            style={{
                              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                              fontFamily: FONTS.sans, fontSize: '13px', color: orange, textDecoration: 'underline',
                            }}
                          >
                            Not them? Clear
                          </button>
                        )}
                      </span>
                      {field.search && !locked ? (
                        <NutritionistSearchInput
                          value={raw}
                          onChange={(v) => setContactField(step.id, field.id, v)}
                          onSelect={(n) => applyNutritionist(step, n)}
                        />
                      ) : (
                        <UnderlineInput
                          type={field.type || 'text'}
                          {...(field.autofill ? NO_AUTOFILL : {})}
                          inputMode={field.type === 'tel' ? 'numeric' : undefined}
                          placeholder={
                            missingFromRecord ? `No ${field.label.toLowerCase()} in our records`
                              : field.placeholder ?? (field.type === 'tel' ? '(555) 123-4567' : undefined)
                          }
                          value={raw}
                          onChange={(e) => setContactField(
                            step.id,
                            field.id,
                            field.type === 'tel' ? formatPhone(e.target.value) : e.target.value,
                          )}
                          readOnly={locked}
                          required={field.required}
                          style={{ fontSize: '24px', color: locked ? '#6d6e75' : dark, cursor: locked ? 'default' : 'text' }}
                        />
                      )}
                      {emailInvalid && (
                        <span style={{ fontFamily: FONTS.sans, fontSize: '13px', color: '#c0392b', display: 'block', marginTop: '6px' }} role="alert">
                          Please use a real email address — we send your nutrition profile there.
                        </span>
                      )}
                      {phoneInvalid && (
                        <span style={{ fontFamily: FONTS.sans, fontSize: '13px', color: '#c0392b', display: 'block', marginTop: '6px' }} role="alert">
                          Please enter a complete 10-digit phone number.
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              );
            })()}

            {error && (
              <p style={{ fontFamily: FONTS.sans, color: '#cc3333', fontSize: '15px', marginTop: '18px' }} role="alert">
                {error}
              </p>
            )}

            {/* OK / submit */}
            <div className="sfq-actions" style={{ marginTop: '34px', display: 'flex', alignItems: 'center', gap: '14px' }}>
              {stepIndex > 0 && (
                <button
                  type="button"
                  className="sfq-back"
                  onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
                  style={{
                    background: 'none', border: `1.5px solid ${OPTION_BORDER}`, borderRadius: '8px',
                    padding: '12px 20px', fontFamily: SERIF, fontSize: '19px', color: dark, cursor: 'pointer',
                  }}
                >
                  ← Back
                </button>
              )}
              <button
                type="button"
                className="sfq-ok"
                // Keep focus in the email input: its onBlur check would disable this button before the click lands.
                onMouseDown={(e) => e.preventDefault()}
                onClick={onNext}
                disabled={!canContinue || busy}
                style={{ ...OK_BTN, opacity: !canContinue || busy ? 0.45 : 1, cursor: !canContinue || busy ? 'default' : 'pointer' }}
              >
                {isLast ? (busy ? 'Saving…' : 'Submit') : (step.type === 'statement' ? (step.buttonLabel || 'Continue') : 'OK')}
              </button>
              {!isLast && step.type !== 'long_text' && step.type !== 'contact' && (
                <span className="sfq-hint" style={{ fontFamily: FONTS.sans, fontSize: '13px', color: '#8d8e94' }}>
                  press <strong>Enter ↵</strong>
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
